// src/features/tasks/components/BasicInformationSection.jsx
import React, { memo, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  FormHelperText,
} from '@mui/material';
import {
  Category as CategoryIcon,
  Flag as FlagIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { FormSection } from '../../../../components/common/form';

const BasicInformationSection = memo(function BasicInformationSection({
  formData,
  onInputChange,
  validationErrors = {},
  loading = false,
  categories = [],
  number,
}) {
  const { t } = useTranslation(['common', 'tasks']);

  const PRIORITY_OPTIONS = [
    { value: 'LOW', label: t('tasks:priority.low'), color: '#4caf50' },
    { value: 'MEDIUM', label: t('tasks:priority.medium'), color: '#ff9800' },
    { value: 'HIGH', label: t('tasks:priority.high'), color: '#f44336' },
    { value: 'CRITICAL', label: t('tasks:priority.critical'), color: '#9c27b0' },
  ];

  const handleFieldChange = useCallback((field) => (event) => {
    const value = event.target?.value !== undefined ? event.target.value : event;
    onInputChange(field, value);
  }, [onInputChange]);

  return (
    <FormSection number={number} title={t('tasks:form.sections.task')}>

        <Box display="flex" flexDirection="column" gap={3}>
          {/* Task Title - Full Width Row */}
          <Box sx={{ width: '100%' }}>
            <TextField
              fullWidth
              label={t('tasks:form.basic_info.task_title')}
              value={formData.title || ''}
              onChange={handleFieldChange('title')}
              error={!!validationErrors.title}
              helperText={validationErrors.title || t('tasks:form.basic_info.task_title_helper')}
              required
              disabled={loading}
              placeholder={t('tasks:form.basic_info.task_title_placeholder')}
              inputProps={{ maxLength: 255 }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontSize: '1.1rem',
                  fontWeight: 500,
                },
              }}
            />
          </Box>

          {/* Description - Full Width Row */}
          <Box sx={{ width: '100%' }}>
            <TextField
              fullWidth
              label={t('common:labels.description')}
              multiline
              minRows={4}
              maxRows={15}
              value={formData.description || ''}
              onChange={handleFieldChange('description')}
              error={!!validationErrors.description}
              helperText={validationErrors.description || t('tasks:form.basic_info.description_helper')}
              disabled={loading}
              placeholder={t('tasks:form.basic_info.description_placeholder')}
              inputProps={{ maxLength: 2000 }}
            />
          </Box>

          {/* Category and Priority - Half Width Each */}
          <Box 
            display="flex" 
            flexWrap="wrap" 
            gap={3}
            sx={{
              '& > *': {
                flex: '1 1 300px', // Flex grow, shrink, and minimum width
                minWidth: '300px',
              }
            }}
          >
            <FormControl error={!!validationErrors.category_id} required>
              <InputLabel id="basic-information-section-category-label">{t('tasks:labels.category')}</InputLabel>
              <Select labelId="basic-information-section-category-label"
                value={formData.category_id || ''}
                onChange={handleFieldChange('category_id')}
                disabled={loading}
                label={t('tasks:labels.category')}
                displayEmpty
                startAdornment={
                  <InputAdornment position="start">
                    <CategoryIcon />
                  </InputAdornment>
                }
              >
                <MenuItem value="" disabled>
                  <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    {t('tasks:form.basic_info.select_category')}
                  </Typography>
                </MenuItem>
                {categories.map((category) => (
                  <MenuItem key={category.category_id} value={category.category_id}>
                    <Box display="flex" alignItems="center" gap={1}>
                      {category.color_code && (
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            borderRadius: '50%',
                            backgroundColor: category.color_code,
                          }}
                        />
                      )}
                      {category.name}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
              {validationErrors.category_id && (
                <FormHelperText>{validationErrors.category_id}</FormHelperText>
              )}
            </FormControl>

            <FormControl>
              <InputLabel id="basic-information-section-priority-label">{t('tasks:labels.priority')}</InputLabel>
              <Select labelId="basic-information-section-priority-label"
                value={formData.priority || 'MEDIUM'}
                onChange={handleFieldChange('priority')}
                disabled={loading}
                label={t('tasks:labels.priority')}
                startAdornment={
                  <InputAdornment position="start">
                    <FlagIcon />
                  </InputAdornment>
                }
              >
                {PRIORITY_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          backgroundColor: option.color,
                        }}
                      />
                      {option.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

    </FormSection>
  );
});

export default BasicInformationSection;